import React, { useState, useMemo } from 'react';
import { 
  ShieldCheck, 
  CheckCircle2, 
  AlertTriangle, 
  Search, 
  Layers, 
  Eye, 
  Cpu, 
  FileSpreadsheet,
  TrendingUp,
  Activity,
  Sliders,
  Check
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  ComposedChart, 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ReferenceLine,
  LabelList,
  Legend
} from 'recharts';
import { Folio } from '../../types.ts';
import { PeriodDataPoint } from '../KpisManager.tsx';

interface SurfaceIntegrityReportViewProps {
  folios: Folio[];
  periodDataList: PeriodDataPoint[];
  periodMode: 'DIAS' | 'SEMANAS' | 'MESES';
  clientName?: string | null;
}

export default function SurfaceIntegrityReportView({
  folios,
  periodDataList,
  periodMode,
  clientName
}: SurfaceIntegrityReportViewProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [substrateFilter, setSubstrateFilter] = useState<'ALL' | 'SCRAP_DANADO' | 'MATERIAL_DANADO' | 'SKIDS' | 'GANCHOS' | 'PIEZAS' | 'MOLDES'>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const rowsPerPage = 10;

  // Real-time synchronization listener for uploaded reworks and scrap
  React.useEffect(() => {
    const handleReload = () => {
      setReloadTrigger(prev => prev + 1);
    };
    window.addEventListener('rework_incidents_updated', handleReload);
    window.addEventListener('storage', handleReload);
    return () => {
      window.removeEventListener('rework_incidents_updated', handleReload);
      window.removeEventListener('storage', handleReload);
    };
  }, []);

  // Load registered incidents from localStorage
  const savedIncidents = useMemo(() => {
    try {
      const raw = localStorage.getItem('sic_pro_rework_incidents') || localStorage.getItem('sic_pro_incidents');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed.filter((inc: any) => 
            !inc.id?.startsWith('INC-2026-') && 
            !inc.id?.startsWith('INC-BMW-')
          );
        }
      }
    } catch (e) {
      console.warn('Error reading saved incidents in SurfaceIntegrityReportView:', e);
    }
    return [];
  }, [reloadTrigger]);

  // Audit records for surface integrity: ONLY contains folios and incidents with Scrap or Damaged Items
  const surfaceRecords = useMemo(() => {
    const list: Array<{
      id: string;
      folioNumber: string;
      date: string;
      clientName: string;
      costCenterName: string;
      totalPieces: number;
      substrateType: 'SKIDS' | 'GANCHOS' | 'PIEZAS' | 'MOLDES';
      substrateLabel: string;
      isCompliant: boolean;
      isScrapDanado: boolean;
      isMaterialDanado: boolean;
      causeCategory?: string;
      originStage?: string;
      integrityScore: number;
      inspector: string;
      verdict: string;
      status: string;
    }> = [];

    const processedFolioIds = new Set<string>();

    folios.forEach(f => {
      // Client filter
      if (clientName && clientName !== 'ALL') {
        const cName = (f.clientName || '').toLowerCase();
        const target = clientName.toLowerCase();
        if (!cName.includes(target) && !target.includes(cName)) {
          return;
        }
      }

      const totalPieces = f.items?.reduce((s, it) => s + (Number(it.quantity) || 0), 0) || 1;
      
      const inspectionStage = f.history?.find(h => h.stage === 'INSPECCION');
      const reworkStage = f.history?.find(h => h.stage === 'RETRABAJO' || h.stage === 'DEVOLUCION');

      // Check linked incidents from localStorage
      const matchingIncident = savedIncidents.find((inc: any) => 
        (inc.folioId && inc.folioId.toLowerCase() === f.id.toLowerCase()) ||
        (inc.id && inc.id.toLowerCase() === f.id.toLowerCase())
      );

      const isScrapOrigin = matchingIncident?.originStage === 'SCRAP_ITEM_DANADO' || 
        matchingIncident?.type === 'SCRAP' ||
        reworkStage?.incidenceReport?.toUpperCase().includes('SCRAP');

      const isMaterialDanado = matchingIncident?.causeCategory === 'MATERIAL DAÑADO' ||
        matchingIncident?.causeCategory?.toUpperCase().includes('MATERIAL DAÑADO') ||
        reworkStage?.incidenceReport?.toUpperCase().includes('MATERIAL DAÑADO');
      
      // Determine if there was any substrate deformation, pitting or chemical attack
      const hasSurfaceDamage = (reworkStage && (
        reworkStage.incidenceReport?.toLowerCase().includes('deform') ||
        reworkStage.incidenceReport?.toLowerCase().includes('pitting') ||
        reworkStage.incidenceReport?.toLowerCase().includes('daño') ||
        reworkStage.incidenceReport?.toLowerCase().includes('golpe') ||
        reworkStage.incidenceReport?.toLowerCase().includes('rugosidad')
      )) || isScrapOrigin || isMaterialDanado;

      // Strictly filter: only include records that are Scrap or Damaged Items
      if (!hasSurfaceDamage && !isScrapOrigin && !isMaterialDanado) {
        return;
      }

      processedFolioIds.add(f.id.toLowerCase());

      // Guess substrate / component family from items or description
      const desc = (f.items?.map(it => it.description).join(' ') || f.notes || '').toLowerCase();
      let substrateType: 'SKIDS' | 'GANCHOS' | 'PIEZAS' | 'MOLDES' = 'PIEZAS';
      let substrateLabel = 'Pieza Automotriz / Chapa';

      if (desc.includes('skid') || desc.includes('bastidor') || desc.includes('carro')) {
        substrateType = 'SKIDS';
        substrateLabel = 'Skids / Bastidores de Pintura';
      } else if (desc.includes('gancho') || desc.includes('hook') || desc.includes('rack') || desc.includes('percha')) {
        substrateType = 'GANCHOS';
        substrateLabel = 'Ganchos / Racks Porta-piezas';
      } else if (desc.includes('molde') || desc.includes('troquel') || desc.includes('herramental')) {
        substrateType = 'MOLDES';
        substrateLabel = 'Moldes y Herramentales';
      }

      const damagedCount = matchingIncident?.affectedPieces 
        ? Number(matchingIncident.affectedPieces) 
        : (reworkStage?.affectedPieces ? Number(reworkStage.affectedPieces) : Math.min(totalPieces, 5));

      let verdict = 'SCRAP / ITEM DAÑADO';
      if (isScrapOrigin && isMaterialDanado) {
        verdict = 'SCRAP/ITEM DAÑADO - MATERIAL DAÑADO';
      } else if (isMaterialDanado) {
        verdict = 'MATERIAL DAÑADO (Sustrato Afectado)';
      } else if (hasSurfaceDamage) {
        verdict = 'Observación Dimensional / Daño Superficial';
      }

      list.push({
        id: f.id,
        folioNumber: (f as any).folioNumber || f.id,
        date: inspectionStage?.timestamp || reworkStage?.timestamp || f.createdDate || '',
        clientName: f.clientName || 'Cliente',
        costCenterName: f.costCenterName || 'Planta',
        totalPieces: damagedCount,
        substrateType,
        substrateLabel,
        isCompliant: false,
        isScrapDanado: isScrapOrigin,
        isMaterialDanado,
        causeCategory: matchingIncident?.causeCategory || (isMaterialDanado ? 'MATERIAL DAÑADO' : 'DEFECTO MECÁNICO'),
        originStage: matchingIncident?.originStage || (isScrapOrigin ? 'SCRAP_ITEM_DANADO' : 'INSPECCION'),
        integrityScore: 85,
        inspector: matchingIncident?.reportedBy || inspectionStage?.operatorName || f.createdBy || 'Inspector de Calidad',
        verdict,
        status: f.status
      });
    });

    // Also include standalone incidents from localStorage not matching any folio in folios
    savedIncidents.forEach((inc: any) => {
      const fId = (inc.folioId || inc.id || '').toLowerCase();
      if (fId && processedFolioIds.has(fId)) return; // already processed
      
      if (clientName && clientName !== 'ALL') {
        const cName = (inc.clientName || '').toLowerCase();
        const target = clientName.toLowerCase();
        if (!cName.includes(target) && !target.includes(cName)) return;
      }

      const isScrapOrigin = inc.originStage === 'SCRAP_ITEM_DANADO' || inc.type === 'SCRAP';
      const isMaterialDanado = inc.causeCategory === 'MATERIAL DAÑADO';
      const isDamaged = isScrapOrigin || isMaterialDanado;

      // Strictly only include scrap/material dañado records
      if (!isDamaged) return;

      let verdict = 'SCRAP / ITEM DAÑADO';
      if (isScrapOrigin && isMaterialDanado) {
        verdict = 'SCRAP/ITEM DAÑADO - MATERIAL DAÑADO';
      } else if (isMaterialDanado) {
        verdict = 'MATERIAL DAÑADO (Sustrato Afectado)';
      }

      list.push({
        id: inc.id,
        folioNumber: inc.folioId || inc.id,
        date: inc.dateReported || new Date().toISOString(),
        clientName: inc.clientName || 'Cliente',
        costCenterName: inc.costCenterName || 'Planta',
        totalPieces: Number(inc.affectedPieces) || 1,
        substrateType: 'PIEZAS',
        substrateLabel: inc.reworkPiecesName || 'Piezas Metálicas Auditadas',
        isCompliant: false,
        isScrapDanado: isScrapOrigin,
        isMaterialDanado,
        causeCategory: inc.causeCategory || (isMaterialDanado ? 'MATERIAL DAÑADO' : 'SCRAP'),
        originStage: inc.originStage || 'SCRAP_ITEM_DANADO',
        integrityScore: 80,
        inspector: inc.reportedBy || 'Inspector Calidad',
        verdict,
        status: inc.status || 'PENDIENTE'
      });
    });

    return list;
  }, [folios, savedIncidents, clientName]);

  // Overall Surface Integrity KPIs: Total delivered vs damaged/scrap with 95% goal
  const metrics = useMemo(() => {
    let totalPiecesDelivered = 0;
    periodDataList.forEach(p => {
      totalPiecesDelivered += p.piecesDelivered;
    });

    if (totalPiecesDelivered === 0) {
      totalPiecesDelivered = folios.reduce((s, f) => s + (f.items?.reduce((is, it) => is + (Number(it.quantity) || 0), 0) || 1), 0);
    }

    const scrapDanadoPieces = surfaceRecords.reduce((sum, r) => sum + r.totalPieces, 0);
    const materialDanadoIncidentsCount = surfaceRecords.filter(r => r.isMaterialDanado || r.causeCategory === 'MATERIAL DAÑADO').length;

    let countSkids = 0;
    let countGanchos = 0;
    let countPiezas = 0;
    let countMoldes = 0;

    surfaceRecords.forEach(r => {
      if (r.substrateType === 'SKIDS') countSkids += r.totalPieces;
      else if (r.substrateType === 'GANCHOS') countGanchos += r.totalPieces;
      else if (r.substrateType === 'MOLDES') countMoldes += r.totalPieces;
      else countPiezas += r.totalPieces;
    });

    const compliantPieces = Math.max(0, totalPiecesDelivered - scrapDanadoPieces);
    const integrityRate = totalPiecesDelivered > 0 
      ? Number(((compliantPieces / totalPiecesDelivered) * 100).toFixed(1))
      : 100;

    const complianceGoal = 95; // Institutional Surface Preservation Target (Meta 95%)

    return {
      totalPiecesInspected: totalPiecesDelivered,
      compliantPieces,
      scrapDanadoPieces,
      materialDanadoIncidentsCount,
      integrityRate,
      complianceGoal,
      countSkids,
      countGanchos,
      countPiezas,
      countMoldes
    };
  }, [surfaceRecords, periodDataList, folios]);

  // Chart Data: Cantidad de piezas entregadas contra piezas dañadas en % (Meta 95%)
  const chartData = useMemo(() => {
    return periodDataList.map(p => {
      const pStart = new Date(p.startDate).getTime();
      const pEnd = new Date(p.endDate).getTime();

      // Find records with scrap or damage in this time window
      const periodScrapRecords = surfaceRecords.filter(r => {
        if (!r.date) return false;
        const d = new Date(r.date).getTime();
        return d >= pStart && d <= pEnd;
      });

      const piezasDanadas = periodScrapRecords.reduce((sum, r) => sum + r.totalPieces, 0);
      const piezasEntregadas = p.piecesDelivered > 0 ? p.piecesDelivered : (p.piecesReceived > 0 ? p.piecesReceived : 100);
      const piezasConformes = Math.max(0, piezasEntregadas - piezasDanadas);

      const porcentajeConformes = piezasEntregadas > 0
        ? Number(((piezasConformes / piezasEntregadas) * 100).toFixed(1))
        : 100;

      const porcentajeDanadas = piezasEntregadas > 0
        ? Number(((piezasDanadas / piezasEntregadas) * 100).toFixed(1))
        : 0;

      return {
        periodLabel: p.periodLabel,
        piezasEntregadas,
        piezasConformes,
        piezasDanadas,
        porcentajeConformes,
        porcentajeDanadas
      };
    });
  }, [periodDataList, surfaceRecords]);

  // Filtered Table
  const filteredList = useMemo(() => {
    let list = surfaceRecords;
    if (substrateFilter === 'SCRAP_DANADO') {
      list = list.filter(r => r.isScrapDanado || r.originStage === 'SCRAP_ITEM_DANADO');
    } else if (substrateFilter === 'MATERIAL_DANADO') {
      list = list.filter(r => r.isMaterialDanado || r.causeCategory === 'MATERIAL DAÑADO');
    } else if (substrateFilter !== 'ALL') {
      list = list.filter(r => r.substrateType === substrateFilter);
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(r => 
        r.folioNumber.toLowerCase().includes(q) ||
        r.clientName.toLowerCase().includes(q) ||
        r.substrateLabel.toLowerCase().includes(q) ||
        r.verdict.toLowerCase().includes(q) ||
        r.inspector.toLowerCase().includes(q)
      );
    }
    return list;
  }, [surfaceRecords, substrateFilter, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredList.length / rowsPerPage));
  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredList.slice(start, start + rowsPerPage);
  }, [filteredList, currentPage]);

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* SUMMARY KPI CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* 1. Índice de Integridad Superficial */}
        <div className={`bg-[#242624] border rounded-2xl p-4 shadow-md ${
          metrics.integrityRate >= metrics.complianceGoal 
            ? 'border-emerald-500/40 bg-gradient-to-b from-emerald-950/25 to-transparent' 
            : 'border-amber-500/40 bg-gradient-to-b from-amber-950/20 to-transparent'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Integridad de Superficie</span>
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-white font-mono flex items-baseline gap-2">
            <span className={metrics.integrityRate >= metrics.complianceGoal ? "text-emerald-400" : "text-amber-400"}>
              {metrics.integrityRate}%
            </span>
            <span className="text-[10px] text-emerald-300 font-bold bg-emerald-900/40 px-1.5 py-0.5 rounded border border-emerald-700">
              Meta 95%
            </span>
          </div>
          <div className="text-[10px] text-slate-400 mt-1 truncate">
            Cero daño térmico ni químico al sustrato
          </div>
        </div>

        {/* 2. Total Piezas Inspeccionadas */}
        <div className="bg-[#242624] border border-[#3a3d3a] rounded-2xl p-4 shadow-md bg-gradient-to-b from-blue-950/20 to-transparent">
          <div className="flex items-center justify-between text-blue-400 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-300">Piezas Auditadas</span>
            <Eye className="h-4 w-4 text-blue-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-white font-mono">
            {metrics.totalPiecesInspected.toLocaleString()}
          </div>
          <div className="text-[10px] text-slate-400 mt-1 truncate">
            Inspección dimensional y visual 100%
          </div>
        </div>

        {/* 3. Scrap / Item Dañado */}
        <div className={`bg-[#242624] border rounded-2xl p-4 shadow-md ${
          metrics.scrapDanadoPieces > 0
            ? 'border-red-500/50 bg-gradient-to-b from-red-950/30 to-transparent'
            : 'border-cyan-500/40 bg-gradient-to-b from-cyan-950/20 to-transparent'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">
              Scrap / Item Dañado
            </span>
            <AlertTriangle className={`h-4 w-4 ${metrics.scrapDanadoPieces > 0 ? 'text-red-400 animate-pulse' : 'text-cyan-400'}`} />
          </div>
          <div className="text-xl sm:text-2xl font-black text-white font-mono flex items-baseline gap-2">
            <span className={metrics.scrapDanadoPieces > 0 ? 'text-red-400' : 'text-cyan-300'}>
              {metrics.scrapDanadoPieces.toLocaleString()} pzs
            </span>
            {metrics.scrapDanadoPieces > 0 && (
              <span className="text-[9.5px] text-red-300 font-bold bg-red-900/50 px-1.5 py-0.5 rounded border border-red-700">
                Alerta
              </span>
            )}
          </div>
          <div className="text-[10px] text-slate-400 mt-1 truncate">
            {metrics.scrapDanadoPieces > 0 ? 'Fase: SCRAP/ITEM DAÑADO • Registro activo' : 'Cero scrap ni piezas dañadas'}
          </div>
        </div>

        {/* 4. Causa: Material Dañado */}
        <div className={`bg-[#242624] border rounded-2xl p-4 shadow-md ${
          metrics.materialDanadoIncidentsCount > 0
            ? 'border-amber-500/50 bg-gradient-to-b from-amber-950/30 to-transparent'
            : 'border-emerald-500/40 bg-gradient-to-b from-emerald-950/20 to-transparent'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">
              Material Dañado
            </span>
            <Check className="h-4 w-4 text-emerald-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-white font-mono flex items-baseline gap-2">
            <span className={metrics.materialDanadoIncidentsCount > 0 ? 'text-amber-400' : 'text-emerald-400'}>
              {metrics.materialDanadoIncidentsCount} {metrics.materialDanadoIncidentsCount === 1 ? 'incidencia' : 'incidencias'}
            </span>
          </div>
          <div className="text-[10px] text-slate-400 mt-1 truncate">
            Causa raíz: Daño mecánico / sustrato reportado
          </div>
        </div>
      </div>

      {/* CHART & SUBSTRATES SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: Chart */}
        <div className="lg:col-span-2 bg-[#202220] border border-[#3a3d3a] rounded-2xl p-4 sm:p-6 shadow-xl space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-[#343634]">
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-400" />
                <span>Piezas Entregadas vs Piezas Dañadas (%)</span>
              </h3>
              <p className="text-xs text-slate-400">
                Métrica en porcentaje (%) de piezas entregadas conformes vs piezas dañadas con etiquetas de datos y meta del 95%.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="h-3 w-3 rounded-xs bg-emerald-500" />
                <span className="text-slate-300">% Entregadas Conformes</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-3 w-3 rounded-xs bg-red-500" />
                <span className="text-red-300 font-bold">% Piezas Dañadas</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-0.5 w-4 border-t-2 border-dashed border-emerald-400" />
                <span className="text-emerald-400 font-bold">Meta 95%</span>
              </div>
            </div>
          </div>

          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 25, right: 15, left: -5, bottom: 15 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#343634" opacity={0.6} />
                <XAxis dataKey="periodLabel" stroke="#8e918e" fontSize={11} tickLine={false} dy={6} />
                <YAxis stroke="#8e918e" fontSize={11} tickLine={false} domain={[0, 105]} tickFormatter={v => `${v}%`} />
                <ReferenceLine 
                  y={95} 
                  stroke="#10b981" 
                  strokeWidth={2} 
                  strokeDasharray="4 4" 
                  label={{ value: 'META 95%', position: 'insideTopRight', fill: '#10b981', fontSize: 11, fontWeight: 'bold' }} 
                />
                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const d = payload[0].payload;
                      return (
                        <div className="bg-[#181918] border border-[#3e413e] rounded-xl p-3.5 shadow-2xl space-y-1.5 text-xs min-w-[240px]">
                          <div className="border-b border-[#303330] pb-1 flex items-center justify-between">
                            <span className="font-bold text-white">{label}</span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                              d.porcentajeConformes >= 95
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                            }`}>
                              {d.porcentajeConformes >= 95 ? 'CUMPLE META ≥ 95%' : 'POR DEBAJO DE META'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-emerald-400">
                            <span>% Entregadas Conformes:</span>
                            <strong className="font-mono text-sm">{d.porcentajeConformes}%</strong>
                          </div>
                          <div className="flex items-center justify-between text-slate-300 text-[11px]">
                            <span>Piezas Conformes:</span>
                            <strong className="font-mono text-white">{d.piezasConformes.toLocaleString()} pzs</strong>
                          </div>
                          <div className="flex items-center justify-between text-red-400 pt-1 border-t border-[#303330]">
                            <span>% Piezas Dañadas:</span>
                            <strong className="font-mono text-sm">{d.porcentajeDanadas}%</strong>
                          </div>
                          <div className="flex items-center justify-between text-slate-300 text-[11px]">
                            <span>Piezas Dañadas / Scrap:</span>
                            <strong className="font-mono text-red-300">{d.piezasDanadas.toLocaleString()} pzs</strong>
                          </div>
                          <div className="flex items-center justify-between text-blue-400 pt-1 border-t border-[#303330]">
                            <span>Total Entregadas:</span>
                            <strong className="font-mono">{d.piezasEntregadas.toLocaleString()} pzs</strong>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend verticalAlign="top" height={36} wrapperStyle={{ paddingBottom: '12px', fontSize: '11px' }} />

                {/* BAR 1: % Piezas Entregadas Conformes con Etiqueta de Datos */}
                <Bar 
                  dataKey="porcentajeConformes" 
                  name="% Piezas Conformes" 
                  fill="#10b981" 
                  radius={[5, 5, 0, 0]} 
                  maxBarSize={36}
                >
                  <LabelList 
                    dataKey="porcentajeConformes" 
                    position="top" 
                    offset={6} 
                    formatter={(val: any) => `${Number(val).toFixed(1)}%`} 
                    style={{ fill: '#34d399', fontSize: 10, fontWeight: 'bold' }} 
                  />
                </Bar>

                {/* BAR 2: % Piezas Dañadas con Etiqueta de Datos */}
                <Bar 
                  dataKey="porcentajeDanadas" 
                  name="% Piezas Dañadas" 
                  fill="#ef4444" 
                  radius={[5, 5, 0, 0]} 
                  maxBarSize={36}
                >
                  <LabelList 
                    dataKey="porcentajeDanadas" 
                    position="top" 
                    offset={6} 
                    formatter={(val: any) => `${Number(val).toFixed(1)}%`} 
                    style={{ fill: '#f87171', fontSize: 10, fontWeight: 'bold' }} 
                  />
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right Col: Substrate Classification */}
        <div className="bg-[#202220] border border-[#3a3d3a] rounded-2xl p-4 sm:p-6 shadow-xl space-y-4">
          <div className="pb-2 border-b border-[#343634]">
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <Layers className="h-5 w-5 text-emerald-400" />
              <span>Sustratos y Geometrías</span>
            </h3>
            <p className="text-xs text-slate-400">
              Desglose de piezas, herramentales y scrap registrado.
            </p>
          </div>

          <div className="space-y-3 pt-2">
            {/* Scrap / Item Dañado Highlight */}
            <div className={`p-2.5 rounded-xl border ${metrics.scrapDanadoPieces > 0 ? 'bg-red-950/30 border-red-900/50' : 'bg-[#181a18] border-[#303330]'}`}>
              <div className="flex items-center justify-between text-xs">
                <span className={`font-bold flex items-center gap-1.5 ${metrics.scrapDanadoPieces > 0 ? 'text-red-400' : 'text-slate-300'}`}>
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span>Scrap / Item Dañado</span>
                </span>
                <span className={`font-mono font-bold ${metrics.scrapDanadoPieces > 0 ? 'text-red-400' : 'text-slate-400'}`}>
                  {metrics.scrapDanadoPieces.toLocaleString()} pzs
                </span>
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">
                {metrics.materialDanadoIncidentsCount} {metrics.materialDanadoIncidentsCount === 1 ? 'incidencia con Material Dañado' : 'incidencias con Material Dañado'}
              </div>
            </div>

            {/* Ganchos */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-200">Ganchos y Racks Porta-piezas</span>
                <span className="font-mono text-emerald-400 font-bold">{metrics.countGanchos.toLocaleString()} pzs</span>
              </div>
              <div className="text-[10px] text-slate-400">Puntas de contacto intactas y sin deformación</div>
            </div>

            {/* Skids */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-200">Skids y Bastidores de Transporte</span>
                <span className="font-mono text-cyan-400 font-bold">{metrics.countSkids.toLocaleString()} pzs</span>
              </div>
              <div className="text-[10px] text-slate-400">Alineación geométrica y planicidad verificada</div>
            </div>

            {/* Piezas Automotrices */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-200">Piezas y Componentes Metálicos</span>
                <span className="font-mono text-blue-400 font-bold">{metrics.countPiezas.toLocaleString()} pzs</span>
              </div>
              <div className="text-[10px] text-slate-400">Sin adelgazamiento de pared ni ataque por grabado ácido</div>
            </div>

            {/* Moldes */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-200">Moldes y Herramentales de Inyección</span>
                <span className="font-mono text-purple-400 font-bold">{metrics.countMoldes.toLocaleString()} pzs</span>
              </div>
              <div className="text-[10px] text-slate-400">Preservación de aristas vivas y pulido óptico</div>
            </div>
          </div>
        </div>
      </div>

      {/* DETAILED SURFACE INSPECTION TABLE */}
      <div className="bg-[#202220] border border-[#3a3d3a] rounded-2xl p-4 sm:p-6 shadow-xl space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-[#343634]">
          <div>
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-red-400" />
              <span>Bitácora de Scrap e Items Dañados ({filteredList.length})</span>
            </h3>
            <p className="text-xs text-slate-400">
              Información y detalle exclusivo de piezas dañadas, scrap reportado y dictamen de no conformidad en sustrato.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                placeholder="Buscar scrap o daño..."
                className="bg-[#161716] border border-[#3e413e] rounded-xl pl-8 pr-3 py-1.5 text-xs text-white placeholder:text-slate-500 focus:border-red-500 focus:outline-none"
              />
            </div>
            <select
              value={substrateFilter}
              onChange={e => { setSubstrateFilter(e.target.value as any); setCurrentPage(1); }}
              className="bg-[#161716] border border-[#3e413e] rounded-xl px-3 py-1.5 text-xs text-white focus:border-red-500 focus:outline-none cursor-pointer"
            >
              <option value="ALL">Todo el Scrap e Items Dañados</option>
              <option value="SCRAP_DANADO">⚠️ Solo Scrap / Item Dañado</option>
              <option value="MATERIAL_DANADO">⚠️ Solo Causa: Material Dañado</option>
              <option value="SKIDS">Skids y Bastidores</option>
              <option value="GANCHOS">Ganchos y Racks</option>
              <option value="PIEZAS">Piezas / Chapa</option>
              <option value="MOLDES">Moldes</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-[#3a3d3a] text-slate-400 uppercase tracking-wider text-[11px] bg-[#1a1c1a]">
                <th className="px-3.5 py-3">Folio</th>
                <th className="px-3 py-3">Cliente</th>
                <th className="px-3 py-3">Tipo de Sustrato / Item</th>
                <th className="px-3 py-3 text-center">Piezas Dañadas</th>
                <th className="px-3.5 py-3">Dictamen Calidad Superficial</th>
                <th className="px-3 py-3">Inspector Calidad</th>
                <th className="px-3 py-3 text-center">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2a2d2a]">
              {paginatedList.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-slate-400">
                    <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2 opacity-80" />
                    <p className="text-xs font-bold text-slate-200">Sin piezas dañadas ni scrap en este periodo</p>
                    <p className="text-[11px] text-slate-500 mt-1 max-w-sm mx-auto">
                      La integridad de superficies se mantiene con cero piezas dañadas y 100% de apego a la meta institucional.
                    </p>
                  </td>
                </tr>
              ) : (
                paginatedList.map(item => (
                  <tr key={item.id} className="hover:bg-[#242624] transition-colors">
                    <td className="px-3.5 py-3 font-mono font-bold text-white whitespace-nowrap">
                      {item.folioNumber}
                    </td>
                    <td className="px-3 py-3 text-slate-300">
                      {item.clientName}
                    </td>
                    <td className="px-3 py-3 text-slate-300">
                      <span className="font-medium text-slate-200">{item.substrateLabel}</span>
                    </td>
                    <td className="px-3 py-3 text-center font-mono font-bold text-blue-400">
                      {item.totalPieces.toLocaleString()} pzs
                    </td>
                    <td className="px-3.5 py-3">
                      {!item.isCompliant || item.isScrapDanado || item.isMaterialDanado ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400 border border-red-500/40">
                          <AlertTriangle className="h-3 w-3" />
                          {item.verdict}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
                          <CheckCircle2 className="h-3 w-3" />
                          {item.verdict}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-slate-400">
                      {item.inspector}
                    </td>
                    <td className="px-3 py-3 text-center text-slate-400 whitespace-nowrap font-mono">
                      {item.date ? new Date(item.date).toLocaleDateString('es-MX') : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 text-xs text-slate-400">
          <div>
            Mostrando <span className="font-bold text-white">{filteredList.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1}</span> a <span className="font-bold text-white">{Math.min(currentPage * rowsPerPage, filteredList.length)}</span> de <span className="font-bold text-white">{filteredList.length}</span> inspecciones
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 bg-[#161716] hover:bg-[#343634] text-slate-300 hover:text-white border border-[#3e413e] rounded-lg disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              Anterior
            </button>
            <span className="px-2 font-mono">Pág. {currentPage} de {totalPages}</span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 bg-[#161716] hover:bg-[#343634] text-slate-300 hover:text-white border border-[#3e413e] rounded-lg disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
